import type { ReactNode } from 'react'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import type { SxProps } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

import AmountPill from './AmountPill'

interface SubscriptionHeroCardProps {
  /** Central hero brand/merchant logo. */
  logo: ReactNode
  /** Subscription/merchant name. */
  name: ReactNode
  /** Price, e.g. "$16.06 / month". Rendered as a dark pill. */
  price: ReactNode
  /** Optional decorative backdrop (e.g. scattered faded logos) behind the hero. */
  backdrop?: ReactNode
  /** Card surface color/background token. */
  bgcolor?: string
  sx?: SxProps
}

/**
 * A hero subscription card: a centered brand logo, name, and price pill over an
 * optional decorative backdrop.
 *
 * Figma: Upcoming Subscription Content (`Card / Content / Slot`) — padding 24,
 * `background.default` surface, a 64px hero `<Logo>`, name (`h2`) and a dark
 * price pill, with faded scattered logos behind.
 *
 * Scope is the Content section only; Header/Description are out of scope.
 */
const SubscriptionHeroCard = ({
  backdrop,
  bgcolor = 'background.default',
  logo,
  name,
  price,
  sx,
}: SubscriptionHeroCardProps) => (
  <Stack sx={{ px: 16, width: '100%', ...sx }}>
    <Box
      sx={{
        bgcolor,
        borderRadius: '8px',
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
      }}
    >
      {backdrop && (
        <Box aria-hidden={true} sx={{ inset: 0, position: 'absolute' }}>
          {backdrop}
        </Box>
      )}
      <Stack sx={{ alignItems: 'center', gap: 12, position: 'relative', px: 24, py: 24 }}>
        <Box sx={{ display: 'flex', flexShrink: 0 }}>{logo}</Box>
        <Stack sx={{ alignItems: 'center', gap: 2 }}>
          <Text bold={true} variant="h2">
            {name}
          </Text>
          <AmountPill px={10} sx={{ alignSelf: 'center' }}>
            {price}
          </AmountPill>
        </Stack>
      </Stack>
    </Box>
  </Stack>
)

export default SubscriptionHeroCard
